/**
 * Quick Order Wholesale — cart-synced order sheet.
 *
 * The quantity inputs ARE the cart (Trade-theme pattern): every change is
 * validated against the row's quantity rules, queued, and flushed as ONE
 * batched POST to /cart/update.js (debounced 250ms). The response cart is
 * the source of truth — inputs resync from it, so stock adjustments and
 * concurrent tabs can never drift. The sticky order bar shows the live
 * line count and total for this sheet.
 */

if (!customElements.get('quick-order-wholesale')) {
  customElements.define('quick-order-wholesale', class QuickOrderWholesale extends HTMLElement {
    static UPDATE_DELAY = 250;

    constructor() {
      super();

      this.table = this.querySelector('.quick-order-wholesale__table');
      // Prefer the toolbar-hosted search (products-toolbar snippet); the
      // in-section input is the fallback when no toolbar renders one.
      const toolbarSearch = document.querySelector('[data-qow-toolbar-search] .quick-order-search__input');
      this.searchInput = toolbarSearch || this.querySelector('.quick-order-search__input');
      if (toolbarSearch) {
        const localSearch = this.querySelector('.quick-order-search');
        if (localSearch) localSearch.style.display = 'none';
      }
      this.sortSelect = document.querySelector('#products-sort-by');
      this.orderBar = this.querySelector('[data-order-bar]');

      this.queue = new Map();
      this.flushTimer = null;
      this.requestInFlight = false;

      this.setupEventListeners();
      this.setupSortListener();
      this.setupFacetListener();
      this.recalcTotals();

      // Re-apply an existing search term after a section refresh (the input
      // lives in the toolbar, which survives facet re-renders)
      if (this.searchInput && this.searchInput.value) {
        this.handleSearch({ target: this.searchInput });
      }
    }

    /* ---------- facet filters ----------
       Facet changes only re-render the products grid section. The table
       respects the same URL filter params server-side, so refresh this
       section via the Section Rendering API when facets update. */
    setupFacetListener() {
      if (QuickOrderWholesale.facetListenerBound) return;
      QuickOrderWholesale.facetListenerBound = true;

      document.addEventListener('on:facet-filters:updated', () => {
        // The facet script pushes the new URL just after dispatching — defer
        // one tick so location.search reflects the applied filters.
        setTimeout(async () => {
          const current = document.querySelector('quick-order-wholesale');
          const sectionEl = current && current.closest('.shopify-section');
          if (!sectionEl) return;
          const sectionId = sectionEl.id.replace(/^shopify-section-/, '');
          const params = new URLSearchParams(window.location.search);
          params.set('section_id', sectionId);
          try {
            const response = await fetch(`${window.location.pathname}?${params.toString()}`);
            if (!response.ok) return;
            const html = await response.text();
            const template = document.createElement('div');
            template.innerHTML = html;
            const fresh = template.querySelector('quick-order-wholesale');
            if (fresh) current.replaceWith(fresh);
          } catch (e) {
            /* leave the current table in place on network errors */
          }
        }, 0);
      });
    }

    setupEventListeners() {
      this.querySelectorAll('.qow-qty-btn').forEach(button => {
        button.addEventListener('click', this.handleStepButton.bind(this));
      });

      this.querySelectorAll('.qow-qty-input').forEach(input => {
        input.addEventListener('change', this.handleQuantityChange.bind(this));
      });

      if (this.searchInput) {
        this.searchInput.addEventListener('input', this.handleSearch.bind(this));
      }

      const removeAll = this.querySelector('[data-remove-all]');
      if (removeAll) {
        removeAll.addEventListener('click', this.handleRemoveAll.bind(this));
      }
    }

    /* ---------- quantity rules ---------- */

    rulesFor(input) {
      return {
        min: parseInt(input.dataset.min, 10) || 1,
        step: parseInt(input.dataset.step, 10) || 1,
        max: input.dataset.max ? parseInt(input.dataset.max, 10) : null
      };
    }

    // Valid values are 0 (not in order) or min + k*step, capped at max
    snapToRules(value, rules) {
      if (!value || value <= 0) return 0;
      let snapped = value;
      if (snapped < rules.min) {
        snapped = rules.min;
      } else {
        snapped = rules.min + Math.round((snapped - rules.min) / rules.step) * rules.step;
      }
      if (rules.max && snapped > rules.max) {
        snapped = rules.min + Math.floor((rules.max - rules.min) / rules.step) * rules.step;
      }
      return snapped;
    }

    handleStepButton(event) {
      event.preventDefault();
      const button = event.currentTarget;
      const input = this.querySelector(`#${button.dataset.target}`);
      if (!input) return;

      const rules = this.rulesFor(input);
      const current = parseInt(input.value, 10) || 0;
      let next;

      if (button.dataset.action === 'increment') {
        next = current === 0 ? rules.min : this.snapToRules(current + rules.step, rules);
      } else {
        next = current <= rules.min ? 0 : this.snapToRules(current - rules.step, rules);
      }

      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    handleQuantityChange(event) {
      const input = event.currentTarget;
      const rules = this.rulesFor(input);
      const snapped = this.snapToRules(parseInt(input.value, 10) || 0, rules);
      input.value = snapped;

      this.queue.set(input.dataset.variantId, snapped);
      this.recalcTotals();
      this.scheduleFlush();
    }

    /* ---------- batched cart sync ---------- */

    scheduleFlush() {
      clearTimeout(this.flushTimer);
      this.flushTimer = setTimeout(() => this.flushQueue(), QuickOrderWholesale.UPDATE_DELAY);
    }

    async flushQueue() {
      if (this.requestInFlight) {
        this.scheduleFlush();
        return;
      }
      if (this.queue.size === 0) return;

      const updates = Object.fromEntries(this.queue);
      this.queue.clear();
      this.requestInFlight = true;
      this.setSyncing(true);

      // Trade-theme pattern: bundle the Section Rendering API into the cart
      // update itself, so the same response carries the re-rendered header
      // cart bubble and cart drawer HTML (no second request).
      const cartDrawer = document.querySelector('cart-drawer');
      const sections = ['cart-icon-bubble'];
      if (cartDrawer) {
        sections.push(cartDrawer.closest('.shopify-section').id.replace('shopify-section-', ''));
      }

      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates,
            sections,
            sections_url: window.location.pathname
          })
        });

        const cart = await response.json();

        if (response.ok) {
          this.showError(null);
          this.resyncFromCart(cart);
          this.notifyCartUpdated(cart);
        } else {
          this.showError(cart.message || cart.description || 'Could not update your order. Please try again.');
          await this.resyncFromServer();
        }
      } catch (error) {
        console.error('Quick order update failed:', error);
        this.showError('Network error. Your last change was not saved.');
        await this.resyncFromServer();
      } finally {
        this.requestInFlight = false;
        this.setSyncing(false);
        if (this.queue.size > 0) this.scheduleFlush();
      }
    }

    // The cart is the source of truth: pull every row's quantity from it.
    // Skips rows with pending (queued) edits so we don't stomp fresh input.
    resyncFromCart(cart) {
      const counts = {};
      (cart.items || []).forEach(item => {
        counts[item.variant_id] = (counts[item.variant_id] || 0) + item.quantity;
      });

      this.querySelectorAll('.qow-qty-input').forEach(input => {
        const id = input.dataset.variantId;
        if (this.queue.has(id)) return;
        input.value = counts[id] || 0;
      });

      this.recalcTotals();
    }

    async resyncFromServer() {
      try {
        const response = await fetch('/cart.js');
        if (response.ok) this.resyncFromCart(await response.json());
      } catch (error) {
        /* keep local state; next successful update resyncs */
      }
    }

    // Swap in the section HTML bundled with the cart response — the same flow
    // the theme's own product form uses (ProductForm.updateCartIcon +
    // cartDrawer.renderContents), so header count and drawer update from the
    // ONE update request without opening the drawer.
    notifyCartUpdated(cart) {
      if (!cart.sections) return;

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(cart, false);
      } else {
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble && cart.sections['cart-icon-bubble']) {
          cartIconBubble.innerHTML = cart.sections['cart-icon-bubble'];
        }
      }
    }

    /* ---------- totals & volume pricing ---------- */

    // Effective unit price for a quantity, honouring volume price breaks
    unitPriceFor(input, quantity) {
      const base = parseInt(input.dataset.price, 10) || 0;
      if (!input.dataset.breaks) return base;
      try {
        let price = base;
        JSON.parse(input.dataset.breaks).forEach(tier => {
          if (quantity >= tier.q) price = tier.p;
        });
        return price;
      } catch (e) {
        return base;
      }
    }

    formatMoney(cents) {
      return 'R' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    recalcTotals() {
      let total = 0;
      let lines = 0;

      this.querySelectorAll('.qow-qty-input').forEach(input => {
        const qty = parseInt(input.value, 10) || 0;
        const row = input.closest('tr');
        const lineTotalEl = row.querySelector('[data-line-total]');
        const unitPriceEl = row.querySelector('[data-unit-price]');
        const unit = this.unitPriceFor(input, qty);

        if (qty > 0) {
          lines++;
          total += qty * unit;
          if (lineTotalEl) lineTotalEl.textContent = this.formatMoney(qty * unit);
          row.classList.add('qow-row--in-order');
        } else {
          if (lineTotalEl) lineTotalEl.textContent = '—';
          row.classList.remove('qow-row--in-order');
        }

        // Show the tier price the buyer is currently paying
        if (unitPriceEl && input.dataset.breaks) {
          unitPriceEl.textContent = this.formatMoney(qty > 0 ? unit : parseInt(input.dataset.price, 10));
        }
      });

      const linesEl = this.querySelector('[data-order-lines]');
      const totalEl = this.querySelector('[data-order-total]');
      if (linesEl) linesEl.textContent = lines;
      if (totalEl) totalEl.textContent = this.formatMoney(total);

      const removeAll = this.querySelector('[data-remove-all]');
      if (removeAll) removeAll.disabled = lines === 0;
    }

    /* ---------- remove all (two-tap confirm) ---------- */

    handleRemoveAll(event) {
      const button = event.currentTarget;

      if (button.dataset.armed !== 'true') {
        button.dataset.armed = 'true';
        button.dataset.originalLabel = button.textContent.trim();
        button.textContent = button.dataset.confirmLabel || 'Remove everything?';
        clearTimeout(this.disarmTimer);
        this.disarmTimer = setTimeout(() => this.disarmRemoveAll(button), 4000);
        return;
      }

      this.disarmRemoveAll(button);
      this.querySelectorAll('.qow-qty-input').forEach(input => {
        if ((parseInt(input.value, 10) || 0) > 0) {
          input.value = 0;
          this.queue.set(input.dataset.variantId, 0);
        }
      });
      this.recalcTotals();
      this.scheduleFlush();
    }

    disarmRemoveAll(button) {
      clearTimeout(this.disarmTimer);
      button.dataset.armed = 'false';
      if (button.dataset.originalLabel) button.textContent = button.dataset.originalLabel;
    }

    /* ---------- feedback ---------- */

    setSyncing(isSyncing) {
      if (this.orderBar) this.orderBar.classList.toggle('qow-orderbar--syncing', isSyncing);
    }

    showError(message) {
      const errorEl = this.querySelector('[data-order-error]');
      if (!errorEl) return;
      if (message) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      } else {
        errorEl.hidden = true;
      }
    }

    /* ---------- search & sort (unchanged behaviour) ---------- */

    handleSearch(event) {
      const searchTerm = event.target.value.toLowerCase();
      this.table.querySelectorAll('tbody tr').forEach(row => {
        const productTitle = (row.dataset.productTitle || '').toLowerCase();
        const sku = (row.dataset.sku || '').toLowerCase();
        row.style.display = productTitle.includes(searchTerm) || sku.includes(searchTerm) ? '' : 'none';
      });
    }

    setupSortListener() {
      if (!this.sortSelect) return;
      this.sortSelect.addEventListener('change', (e) => {
        const sortValue = e.detail?.selectedValue || e.target.value;
        this.sortTable(sortValue);
      });
    }

    sortTable(sortBy) {
      const tbody = this.table.querySelector('tbody');
      const rows = Array.from(tbody.querySelectorAll('.qow-row'));

      rows.sort((a, b) => {
        const titleA = a.dataset.productTitle.toLowerCase();
        const titleB = b.dataset.productTitle.toLowerCase();
        const priceA = parseFloat(a.querySelector('.qow-qty-input')?.dataset.price || 0);
        const priceB = parseFloat(b.querySelector('.qow-qty-input')?.dataset.price || 0);

        switch (sortBy) {
          case 'title-ascending': return titleA.localeCompare(titleB);
          case 'title-descending': return titleB.localeCompare(titleA);
          case 'price-ascending': return priceA - priceB;
          case 'price-descending': return priceB - priceA;
          default: return 0;
        }
      });

      rows.forEach(row => tbody.appendChild(row));
    }
  });
}
